import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router";

import type { ProjectEditRequest } from "@/api/types";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { DEFAULT_APP_BRANDING } from "@/helpers/app-branding";
import { canManageProjectManagement } from "@/helpers/permissions";
import { useLinkedProjects } from "@/hooks/Network/useLinkedProjects";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { usePermission } from "@/hooks/usePermission";
import { useConfigurationQueries } from "@/queries/configurations/queryOptions";
import { projectMutations } from "@/queries/project/mutations";
import { useProjectQueries } from "@/queries/project/queryOptions";

import { type CreateOrEditProjectFormValues, ProjectForm, type ProjectFormProps } from "./ProjectForm";

export function EditProjectPage(): React.ReactNode {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();
  const hasPermission = usePermission();

  const projectQueries = useProjectQueries();
  const {
    data: projectDetails,
    isFetching: isLoadingProjectDetails,
    error: projectDetailsError,
  } = useQuery({
    ...projectQueries.details(),
  });
  const sessionUser = useSessionUser();
  const { data: projects = [], isPending: isLoadingProjects, error: projectsError } = useLinkedProjects();
  const {
    data: baseLanguages,
    isFetching: isFetchingBaseLanguages,
    error: baseLanguagesError,
  } = useQuery({
    ...projectQueries.languagesBase(),
    enabled: hasPermission(canManageProjectManagement),
  });
  const configurationQueries = useConfigurationQueries();
  const {
    data: appBrands,
    isPending: isPendingAppBrands,
    error: appBrandsError,
  } = useQuery(configurationQueries.listAppBrandings());

  const editProjectMutation = projectMutations.useEditProject();
  const closeProject = projectMutations.useCloseProject();

  const handleSubmit = async (formValues: CreateOrEditProjectFormValues) => {
    setIsSubmitting(true);

    if (!projectDetails) {
      setIsSubmitting(false);

      return;
    }

    const payload: ProjectEditRequest = {
      city: formValues.city,
      maintenance: formValues.maintenance,
      name: formValues.name,
      owner: formValues.owner,
      projectType: formValues.type,
      slug: formValues.slug,
      activeState: formValues.activeState,
      appBranding: formValues.appBranding,
      brandedAppPrompt: formValues.appBranding !== DEFAULT_APP_BRANDING ? "mandatory" : undefined,
      developmentType: formValues.developmentType,
      allowHidePostForAdmin: formValues.allowHidePostForAdmin === "true",
      housemateInvitesEnabled: formValues.housemateInvitesEnabled === "true",
      hideYoutubeLinksInInvitationEmails: projectDetails.hideYoutubeLinksInInvitationEmails,
      customCodeOfConductId: projectDetails.codeOfConduct.isCustom ? projectDetails.codeOfConduct.id : undefined,
    };

    const editResult = await editProjectMutation.mutateAsync(payload);
    if (editResult.data.slug === projectDetails.slug) {
      void navigate(`/@/${editResult.data.slug}`);
    } else {
      globalThis.location.href = `/@/${editResult.data.slug}`;
    }

    setIsSubmitting(false);
  };

  const error = projectDetailsError || baseLanguagesError || projectsError || appBrandsError;
  if (error) {
    return <ErrorPage error={error} />;
  }

  const isLoading = isLoadingProjectDetails || isFetchingBaseLanguages || isPendingAppBrands || isLoadingProjects;
  if (isLoading) {
    return <FullSizeLoader withPadding />;
  }

  if (!projectDetails) {
    return <ErrorPage status={404} />;
  }

  const defaultLanguage = sessionUser.language;

  const formProps: ProjectFormProps = {
    isEditMode: true,
    defaultValues: {
      city: projectDetails.city,
      owner: projectDetails.owner,
      maintenance: projectDetails.maintenance,
      language: baseLanguages?.find((x) => x.id === projectDetails.languageId) || defaultLanguage,
      name: projectDetails.name,
      slug: projectDetails.slug,
      type: projectDetails.type,
      activeState: projectDetails.activeState,
      appBranding: projectDetails.appBranding,
      developmentType: projectDetails.developmentType,
      allowHidePostForAdmin: projectDetails.allowHidePostForAdmin ? "true" : "false",
      housemateInvitesEnabled: projectDetails.housemateInvitesEnabled ? "true" : "false",
    },
    baseLanguages: baseLanguages || [],
    projects,
    projectState: projectDetails?.activeState || "unknown",
    isSubmitting,
    closeProject: closeProject.mutateAsync,
    isClosingProject: closeProject.isPending,
    onSubmit: handleSubmit,
    appBrands,
  };

  return <ProjectForm {...formProps} />;
}
